import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { CreditCard, Search, Download, ArrowUpDown, ArrowUp, ArrowDown, FileText, Plus, X, Save, Send, Trash2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatPrice } from "../utils/price";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortHeader = ({ label, field, sortField, sortDir, onSort }) => {
  const active = sortField === field;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#4A5568] cursor-pointer select-none hover:text-[#08707A] transition-colors whitespace-nowrap"
      onClick={() => onSort(field)}
      data-testid={`sort-tx-${field}`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        {active ? (
          sortDir === "asc" ? <ArrowUp size={13} className="text-[#1CA3B0]" /> : <ArrowDown size={13} className="text-[#1CA3B0]" />
        ) : (
          <ArrowUpDown size={13} className="text-slate-300" />
        )}
      </span>
    </th>
  );
};

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState("");
  const [filterMethod, setFilterMethod] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    event_id: "", event_title: "", customer_name: "", customer_email: "",
    amount: "", currency: "eur", payment_method: "cash", payment_status: "paid", notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [txRes, evRes] = await Promise.all([
        axios.get(`${API}/transactions`),
        axios.get(`${API}/events`),
      ]);
      setTransactions(txRes.data);
      setEvents(evRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const handleDownloadInvoice = async (txId) => {
    try {
      const res = await axios.get(`${API}/transactions/${txId}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Fature-${txId.slice(0, 8).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Gabim gjate shkarkimit te fatures.");
    }
  };

  const handleSendInvoice = async (txId) => {
    setSending(txId);
    try {
      await axios.post(`${API}/transactions/${txId}/send-invoice`);
      alert("Fatura u dergua me sukses!");
    } catch (err) {
      alert(err.response?.data?.detail || "Dergimi deshtoi. Kontrolloni SMTP.");
    } finally {
      setSending(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/transactions`, {
        ...form,
        amount: parseFloat(form.amount) || 0,
      });
      fetchData();
      setShowForm(false);
      setForm({ event_id: "", event_title: "", customer_name: "", customer_email: "", amount: "", currency: "eur", payment_method: "cash", payment_status: "paid", notes: "" });
    } catch (err) {
      console.error(err);
      alert("Gabim gjate shtimit te transaksionit.");
    }
  };

  const handleExport = () => {
    const csv = ["Nr,Klienti,Email,Event,Shuma,Metoda,Statusi,Data"].concat(
      sorted.map((tx, i) => `${i + 1},"${tx.customer_name}","${tx.customer_email}","${tx.event_title}","${Number(tx.amount||0).toFixed(2).replace(".",",")} €","${tx.payment_method || 'stripe'}","${tx.payment_status}","${formatDate(tx.created_at)}"`)
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "transaksione.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (ids) => {
    setSelectedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Do fshihen ${selectedIds.size} transaksione. Kjo veprim nuk mund te ktehet mbrapsht. Vazhdo?`)) return;
    setDeleting(true);
    try {
      await Promise.all(Array.from(selectedIds).map((id) => axios.delete(`${API}/transactions/${id}`)));
      setTransactions((prev) => prev.filter((t) => !selectedIds.has(t.id)));
      setSelectedIds(new Set());
    } catch (err) {
      alert("Gabim gjate fshirjes. Rifresko dhe provo perseri.");
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const filtered = transactions.filter((tx) => {
    const s = search.toLowerCase();
    const matchSearch = !s || `${tx.customer_name} ${tx.customer_email} ${tx.event_title}`.toLowerCase().includes(s);
    const matchMethod = filterMethod === "all" || (tx.payment_method || "stripe") === filterMethod;
    const matchStatus = filterStatus === "all" || tx.payment_status === filterStatus;
    return matchSearch && matchMethod && matchStatus;
  });

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let valA = a[sortField] || "";
      let valB = b[sortField] || "";
      if (sortField === "created_at") { valA = new Date(valA); valB = new Date(valB); }
      else if (sortField === "amount") { valA = parseFloat(valA) || 0; valB = parseFloat(valB) || 0; }
      else { valA = String(valA).toLowerCase(); valB = String(valB).toLowerCase(); }
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortField, sortDir]);

  const methodColors = {
    stripe: { bg: "#EDE9FE", text: "#6B21A8", label: "Stripe" },
    paypal: { bg: "#DBEAFE", text: "#1E40AF", label: "PayPal" },
    bank: { bg: "#E6F7F8", text: "#08707A", label: "Transfer Bankar" },
    cash: { bg: "#F0FAF0", text: "#2D6A2E", label: "Cash" },
    pok: { bg: "#E6F7F8", text: "#08707A", label: "Karte krediti/debiti" },
  };

  const statusColors = {
    paid: { bg: "#DCFCE7", text: "#166534", label: "Paguar" },
    pending: { bg: "#FEF3C7", text: "#92400E", label: "Ne pritje" },
    expired: { bg: "#FEE2E2", text: "#991B1B", label: "Skaduar" },
    refunded: { bg: "#F1F5F9", text: "#475569", label: "Rimbursuar" },
  };

  const totalPaid = transactions.filter(t => t.payment_status === "paid").reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
  const totalPending = transactions.filter(t => t.payment_status === "pending").reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
  const fmt = (n) => Number(n || 0).toFixed(2).replace(".", ",");

  return (
    <div data-testid="admin-transactions">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Inter_Tight']">Transaksionet</h1>
          <p className="text-sm text-[#828D9E] mt-1">{transactions.length} transaksione totale</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={deleting}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50"
              data-testid="bulk-delete-tx-btn"
            >
              <Trash2 size={14} /> {deleting ? "Duke fshire..." : `Fshi ${selectedIds.size} te zgjedhurat`}
            </button>
          )}
          <button onClick={handleExport} className="rounded-lg px-4 py-2 text-sm font-semibold border border-slate-200 text-[#4A5568] hover:bg-slate-50 inline-flex items-center gap-2" data-testid="export-tx-btn">
            <Download size={14} /> Eksporto CSV
          </button>
          <button onClick={() => setShowForm(true)} className="rounded-lg px-5 py-2 text-sm font-semibold text-white inline-flex items-center gap-2 bg-[#08707A] hover:bg-[#065A63]" data-testid="add-tx-btn">
            <Plus size={14} /> Shto Transaksion
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="tx-total-card">
          <p className="text-xs font-bold uppercase tracking-wider text-[#828D9E] mb-1">Totale Transaksione</p>
          <p className="text-2xl font-black text-[#003F72] font-['Inter_Tight']">{transactions.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="tx-paid-card">
          <p className="text-xs font-bold uppercase tracking-wider text-[#828D9E] mb-1">Shuma e Paguar</p>
          <p className="text-2xl font-black text-[#166534] font-['Inter_Tight']">{fmt(totalPaid)} €</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="tx-pending-card">
          <p className="text-xs font-bold uppercase tracking-wider text-[#828D9E] mb-1">Ne Pritje</p>
          <p className="text-2xl font-black text-[#92400E] font-['Inter_Tight']">{fmt(totalPending)} €</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#828D9E]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kerko sipas emrit, emailit, eventit..." className="pl-9 rounded-lg border-slate-200 bg-white" data-testid="search-tx" />
        </div>
        <select value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white" data-testid="filter-method">
          <option value="all">Te gjitha metodat</option>
          <option value="stripe">Stripe</option>
          <option value="paypal">PayPal</option>
          <option value="bank">Transfer Bankar</option>
          <option value="cash">Cash</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white" data-testid="filter-status">
          <option value="all">Te gjithe statuset</option>
          <option value="paid">Paguar</option>
          <option value="pending">Ne pritje</option>
          <option value="expired">Skaduar</option>
        </select>
      </div>

      {/* Add Manual Transaction Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tx-form-modal">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-lg font-bold">Shto Transaksion Manual</h3>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <Label className="text-xs font-semibold mb-1 block">Eventi *</Label>
                <select value={form.event_id} onChange={(e) => {
                  const ev = events.find(ev => ev.id === e.target.value);
                  setForm({ ...form, event_id: e.target.value, event_title: ev?.title || "", amount: ev?.price || form.amount });
                }} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm" data-testid="tx-event-select">
                  <option value="">Zgjidh eventin...</option>
                  {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title} - {formatPrice(ev.price)}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold mb-1 block">Emri i klientit *</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Emri Mbiemri" className="rounded-lg border-slate-200 bg-slate-50" data-testid="tx-customer-name" /></div>
                <div><Label className="text-xs font-semibold mb-1 block">Email *</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="rounded-lg border-slate-200 bg-slate-50" data-testid="tx-customer-email" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold mb-1 block">Shuma (EUR) *</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-lg border-slate-200 bg-slate-50" data-testid="tx-amount" /></div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Metoda e Pageses *</Label>
                  <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm" data-testid="tx-payment-method">
                    <option value="cash">Cash</option>
                    <option value="bank">Transfer Bankar</option>
                    <option value="paypal">PayPal</option>
                    <option value="stripe">Stripe</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Statusi</Label>
                <select value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm" data-testid="tx-status-select">
                  <option value="paid">Paguar</option>
                  <option value="pending">Ne pritje</option>
                </select>
              </div>
              <div><Label className="text-xs font-semibold mb-1 block">Shenime</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Shenime shtese..." className="rounded-lg border-slate-200 bg-slate-50" data-testid="tx-notes" /></div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="rounded-lg px-6 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-2" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }} data-testid="save-tx-btn"><Save size={14} /> Ruaj</button>
                <button type="button" onClick={() => setShowForm(false)} className="rounded-lg px-6 py-2.5 text-sm font-semibold border border-slate-200 text-[#4A5568]">Anulo</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="transactions-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    className="accent-[#1CA3B0] cursor-pointer"
                    checked={sorted.length > 0 && sorted.every((tx) => selectedIds.has(tx.id))}
                    onChange={() => toggleSelectAllVisible(sorted.map((t) => t.id))}
                    data-testid="select-all-tx"
                    title="Zgjidh te gjitha"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#4A5568] w-12">Nr</th>
                <SortHeader label="Klienti" field="customer_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Event / Kurs" field="event_title" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Shuma" field="amount" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Metoda" field="payment_method" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Statusi" field="payment_status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Data" field="created_at" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-[#4A5568] w-32">Veprime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-16 text-[#828D9E]">
                  <CreditCard className="mx-auto mb-3 text-slate-300" size={40} />
                  <p>Nuk ka transaksione.</p>
                </td></tr>
              ) : sorted.map((tx, idx) => {
                const mc = methodColors[tx.payment_method] || methodColors.stripe;
                const sc = statusColors[tx.payment_status] || statusColors.pending;
                const isSelected = selectedIds.has(tx.id);
                return (
                  <tr key={tx.id} className={`transition-colors ${isSelected ? "bg-[#1CA3B0]/5" : "hover:bg-slate-50/50"}`} data-testid={`tx-row-${tx.id}`}>
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        className="accent-[#1CA3B0] cursor-pointer"
                        checked={isSelected}
                        onChange={() => toggleSelect(tx.id)}
                        data-testid={`select-tx-${tx.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-[#828D9E] font-mono">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-[#0A0D10]">{tx.customer_name || "-"}</p>
                      <p className="text-xs text-[#828D9E]">{tx.customer_email}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#4A5568] max-w-[200px] truncate">{tx.event_title || "-"}</td>
                    <td className="px-4 py-3 text-sm font-bold text-[#003F72] whitespace-nowrap">{fmt(tx.amount)} €</td>
                    <td className="px-4 py-3">
                      <span className="inline-block text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: mc.bg, color: mc.text }}>
                        {mc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: sc.bg, color: sc.text }}>
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#4A5568] whitespace-nowrap">{formatDate(tx.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleDownloadInvoice(tx.id)}
                          className="p-1.5 text-[#08707A] hover:bg-[#08707A]/10 rounded-md transition-colors"
                          title="Shkarko Faturen PDF"
                          data-testid={`download-invoice-${tx.id}`}
                        >
                          <FileText size={15} />
                        </button>
                        <button
                          onClick={() => handleSendInvoice(tx.id)}
                          disabled={sending === tx.id}
                          className="p-1.5 text-[#1CA3B0] hover:bg-[#1CA3B0]/10 rounded-md transition-colors disabled:opacity-50"
                          title="Dergo Faturen me Email"
                          data-testid={`send-invoice-${tx.id}`}
                        >
                          <Send size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminTransactions;
